import { useState } from 'react'
import { Phone, CalendarDays, ChevronRight, X } from 'lucide-react'
import { PageSpinner } from '@/components/ui/Spinner'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { useStoreOrders, useUpdateStoreOrderStatus } from '@/hooks/useStoreOrders'
import type { StoreOrder, StoreOrderStatus } from '@/types/app'

const STATUS_LABELS: Record<StoreOrderStatus, string> = {
  placed:    'Placed',
  confirmed: 'Confirmed',
  ready:     'Ready',
  completed: 'Completed',
  cancelled: 'Cancelled',
}
const STATUS_VARIANTS: Record<StoreOrderStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  placed:    'warning',
  confirmed: 'info',
  ready:     'info',
  completed: 'success',
  cancelled: 'danger',
}
// What "the next step" is from each status — drives the single primary
// action button so staff don't have to think about the lifecycle, just
// keep clicking the one obvious next thing.
const NEXT_STATUS: Partial<Record<StoreOrderStatus, { label: string; to: StoreOrderStatus }>> = {
  placed:    { label: 'Confirm Order',  to: 'confirmed' },
  confirmed: { label: 'Mark Ready',     to: 'ready' },
  ready:     { label: 'Mark Completed', to: 'completed' },
}

// Web/storefront orders — a simple placed → confirmed → ready → completed
// lifecycle, deliberately separate from the Catering page's inquiry/quote
// negotiation flow. No editing here: price and items are exactly what the
// customer ordered and paid the storefront's listed price for.
export function WebOrdersPage() {
  const { data: orders = [], isLoading } = useStoreOrders()
  const { mutateAsync: updateStatus, isPending: updating } = useUpdateStoreOrderStatus()
  const [selected, setSelected] = useState<StoreOrder | null>(null)

  const activeOrders = orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled')
  const pastOrders = orders.filter(o => o.status === 'completed' || o.status === 'cancelled')

  if (isLoading) return <PageSpinner />

  function OrderRow({ order }: { order: StoreOrder }) {
    const next = NEXT_STATUS[order.status]
    return (
      <tr className="hover:bg-stone-50 cursor-pointer transition-colors" onClick={() => setSelected(order)}>
        <td className="px-4 py-3">
          <span className="font-mono text-xs text-stone-500">{order.order_number ?? '—'}</span>
        </td>
        <td className="px-4 py-3">
          <p className="font-medium text-stone-900">{order.client_name}</p>
          <p className="text-xs text-stone-400">{order.client_phone}</p>
        </td>
        <td className="px-4 py-3 text-sm text-stone-600">
          {order.fulfillment_type === 'delivery' ? 'Delivery' : order.fulfillment_type === 'pickup' ? 'Pickup' : '—'}
          {order.requested_date && <span className="text-stone-400"> · {order.requested_date}</span>}
        </td>
        <td className="px-4 py-3 text-right font-medium text-stone-900">
          {order.total_amount != null ? `$${order.total_amount.toFixed(2)}` : '—'}
        </td>
        <td className="px-4 py-3">
          <Badge variant={STATUS_VARIANTS[order.status]}>{STATUS_LABELS[order.status]}</Badge>
        </td>
        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
          {next && (
            <Button size="sm" variant="outline" loading={updating} onClick={() => updateStatus({ id: order.id, status: next.to })}>
              {next.label}
            </Button>
          )}
        </td>
        <td className="px-3 py-3"><ChevronRight size={14} className="text-stone-400" /></td>
      </tr>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="page-header">
        <h1 className="text-xl font-semibold text-stone-900">Web Orders</h1>
        <p className="text-sm text-stone-400">Orders placed directly through your online store.</p>
      </div>

      {orders.length === 0 ? (
        <div className="card p-12 text-center text-stone-400 text-sm">
          No web orders yet. Share your storefront link from Settings to start taking orders online.
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Order #</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Customer</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Fulfillment</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Total</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-2.5" />
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {activeOrders.map(order => <OrderRow key={order.id} order={order} />)}
              </tbody>
            </table>
          </div>

          {pastOrders.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-stone-400">Completed / Cancelled</h2>
              <div className="card overflow-hidden">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-stone-100">
                    {pastOrders.map(order => <OrderRow key={order.id} order={order} />)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)}>
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-semibold text-stone-900 font-mono text-sm">{selected.order_number}</h3>
                <Badge variant={STATUS_VARIANTS[selected.status]}>{STATUS_LABELS[selected.status]}</Badge>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 rounded hover:bg-stone-100 text-stone-400">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="space-y-1">
                <p className="font-medium text-stone-900">{selected.client_name}</p>
                <p className="text-sm text-stone-500 flex items-center gap-1.5"><Phone size={12} />{selected.client_phone}</p>
                {selected.client_email && <p className="text-sm text-stone-500">{selected.client_email}</p>}
                <p className="text-sm text-stone-500 flex items-center gap-1.5">
                  <CalendarDays size={12} />
                  {selected.fulfillment_type === 'delivery' ? 'Delivery' : 'Pickup'}
                  {selected.requested_date && ` · ${selected.requested_date}`}
                </p>
                {selected.venue_address && <p className="text-xs text-stone-400">{selected.venue_address}</p>}
              </div>

              <div className="border border-stone-200 rounded-lg divide-y divide-stone-100">
                {(selected.items ?? []).map(it => (
                  <div key={it.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-stone-700">{it.name} × {it.quantity}</span>
                    <span className="font-medium text-stone-900">${(it.unit_price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-1 text-sm">
                {selected.subtotal != null && (
                  <div className="flex justify-between text-stone-500"><span>Subtotal</span><span>${selected.subtotal.toFixed(2)}</span></div>
                )}
                {selected.tax_amount != null && (
                  <div className="flex justify-between text-stone-500"><span>+ 7% tax</span><span>${selected.tax_amount.toFixed(2)}</span></div>
                )}
                {selected.total_amount != null && (
                  <div className="flex justify-between font-bold text-stone-900 pt-1 border-t border-stone-100"><span>Total</span><span>${selected.total_amount.toFixed(2)}</span></div>
                )}
              </div>

              {selected.notes && (
                <div>
                  <p className="text-xs font-medium text-stone-500 mb-1">Customer Notes</p>
                  <p className="text-sm text-stone-700">{selected.notes}</p>
                </div>
              )}

              {NEXT_STATUS[selected.status] && (
                <Button
                  className="w-full"
                  loading={updating}
                  onClick={async () => {
                    const next = NEXT_STATUS[selected.status]!
                    const updated = await updateStatus({ id: selected.id, status: next.to })
                    setSelected(updated)
                  }}
                >
                  {NEXT_STATUS[selected.status]!.label}
                </Button>
              )}
              {selected.status !== 'cancelled' && selected.status !== 'completed' && (
                <button
                  className="w-full text-center text-xs text-stone-400 hover:text-danger"
                  onClick={async () => {
                    const updated = await updateStatus({ id: selected.id, status: 'cancelled' })
                    setSelected(updated)
                  }}
                >
                  Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
